import { createSlice } from '@reduxjs/toolkit';

import type { Arbitro, ArbitrosState, Especialidade } from '@/types/arbitro/arbitroTypes';

import {
  alterarStatusArbitro,
  alternarStatusEspecialidade,
  atualizarArbitro,
  atualizarPesosArbitros,
  criarArbitro,
  criarEspecialidade,
  listarArbitros,
  listarEspecialidadesArbitrais,
  registrarIndisponibilidadeArbitro,
  removerIndisponibilidadeArbitro,
  renomearEspecialidade,
  reenviarConviteArbitro,
} from './actions';

const initialState: ArbitrosState = {
  arbitros: [],
  especialidades: [],
  currentArbitro: null,
  isLoading: false,
  isSaving: false,
  error: null,
};

const upsertArbitro = (state: ArbitrosState, arbitro: Arbitro) => {
  const index = state.arbitros.findIndex((item) => item.id === arbitro.id);
  if (index !== -1) {
    state.arbitros[index] = arbitro;
  } else {
    state.arbitros.unshift(arbitro);
  }
  if (state.currentArbitro?.id === arbitro.id) {
    state.currentArbitro = arbitro;
  }
};

const upsertEspecialidade = (state: ArbitrosState, especialidade: Especialidade) => {
  const index = state.especialidades.findIndex((item) => item.id === especialidade.id);
  if (index !== -1) {
    state.especialidades[index] = especialidade;
  } else {
    state.especialidades.push(especialidade);
  }
};

const arbitrosSlice = createSlice({
  name: 'arbitros',
  initialState,
  reducers: {
    clearArbitrosError: (state) => {
      state.error = null;
    },
    setCurrentArbitro: (state, action: { payload: Arbitro | null }) => {
      state.currentArbitro = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(listarArbitros.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(listarArbitros.fulfilled, (state, action) => {
        state.isLoading = false;
        state.arbitros = action.payload;
      })
      .addCase(listarArbitros.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(listarEspecialidadesArbitrais.fulfilled, (state, action) => {
        state.especialidades = action.payload;
      })

      .addCase(criarEspecialidade.fulfilled, (state, action) => {
        upsertEspecialidade(state, action.payload);
      })
      .addCase(criarEspecialidade.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(renomearEspecialidade.fulfilled, (state, action) => {
        upsertEspecialidade(state, action.payload);
      })
      .addCase(renomearEspecialidade.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(alternarStatusEspecialidade.fulfilled, (state, action) => {
        upsertEspecialidade(state, action.payload);
      })
      .addCase(alternarStatusEspecialidade.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      .addCase(criarArbitro.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(criarArbitro.fulfilled, (state, action) => {
        state.isSaving = false;
        upsertArbitro(state, action.payload);
      })
      .addCase(criarArbitro.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      })

      .addCase(atualizarArbitro.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(atualizarArbitro.fulfilled, (state, action) => {
        state.isSaving = false;
        upsertArbitro(state, action.payload);
      })
      .addCase(atualizarArbitro.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      })

      .addCase(alterarStatusArbitro.fulfilled, (state, action) => {
        upsertArbitro(state, action.payload);
      })
      .addCase(alterarStatusArbitro.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      .addCase(atualizarPesosArbitros.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(atualizarPesosArbitros.fulfilled, (state, action) => {
        state.isSaving = false;
        state.arbitros = action.payload;
      })
      .addCase(atualizarPesosArbitros.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      })

      .addCase(registrarIndisponibilidadeArbitro.fulfilled, (state, action) => {
        upsertArbitro(state, action.payload);
      })
      .addCase(registrarIndisponibilidadeArbitro.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      .addCase(removerIndisponibilidadeArbitro.fulfilled, (state, action) => {
        upsertArbitro(state, action.payload);
      })
      .addCase(removerIndisponibilidadeArbitro.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      .addCase(reenviarConviteArbitro.fulfilled, (state, action) => {
        upsertArbitro(state, action.payload);
      });
  },
});

export const { clearArbitrosError, setCurrentArbitro } = arbitrosSlice.actions;
export default arbitrosSlice.reducer;
